import { AuthedButton } from '../authed-button';
import { SpanLoading } from '../fetch-status/span-loading';
import { ButtonProps } from '../ui/button';
import { InteractWatchedIcon, InteractWatchIcon, InteractWatchingIcon } from '@/client/assets';
import { IS_DEBUG } from '@/client/confs/app';
import { cn, getErrorMessage } from '@/client/utils';
import { api } from '@/client/utils/api';
import { queryClient } from '@/client/utils/query-client';
import { MARKET_DETAIL_QUERY } from '@/libs/react-sdk/api/use-market-detail';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useHoverDirty from 'react-use/lib/useHoverDirty';
import { toast } from 'sonner';

// The event and market pages read the follow flag from the MarketDetail entry of the market.
// Refresh only the entries of this market. The home lists also hold the flag, but they hold 48 markets.
const refreshFollowFlag = (address: string) =>
  queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      queryKey[1] === MARKET_DETAIL_QUERY &&
      String((queryKey[2] as { marketAddress?: unknown } | undefined)?.marketAddress).toLowerCase() ===
        address.toLowerCase(),
  });

type FollowMarketProps = ButtonProps & {
  isFollowed: boolean;
  address: string;
  iconClassName?: string;
  watchIcon?: ReactNode;
  watchingIcon?: ReactNode;
  watchedIcon?: ReactNode;
};

export const FollowMarket = ({
  address,
  isFollowed,
  iconClassName,
  watchingIcon = <InteractWatchingIcon className={cn('size-4  fill-current', iconClassName)} />,
  watchIcon = <InteractWatchIcon className={cn('size-4 fill-current', iconClassName)} />,
  watchedIcon = <InteractWatchedIcon className={cn('size-4 fill-current', iconClassName)} />,
  ...props
}: FollowMarketProps) => {
  const { t } = useTranslation();
  const [isFollow, setIsFollow] = useState(isFollowed);

  // Sync local state when prop changes (e.g., after refetch or remount)
  useEffect(() => {
    setIsFollow(isFollowed);
  }, [isFollowed]);

  const ref = useRef<HTMLButtonElement>(null);
  const isHover = useHoverDirty(ref);
  const [isLoading, setIsLoading] = useState(false);

  const handleFollow = async () => {
    setIsLoading(true);
    try {
      await api.market.followMarket({}, { marketAddress: address });
      void refreshFollowFlag(address);
      setIsFollow(true);
      toast.success(t('followed'));
    } catch (error) {
      if (IS_DEBUG) console.log('followMarket error:', error);
      const msg = getErrorMessage(error);
      toast.error(msg ?? t('follow-failed'));
    }
    setIsLoading(false);
  };
  const handleUnFollow = async () => {
    setIsLoading(true);
    try {
      await api.market.unfollowMarket({}, { marketAddress: address });
      void refreshFollowFlag(address);
      setIsFollow(false);
      toast.success(t('unfollowed'));
    } catch (error) {
      if (IS_DEBUG) console.log('unfollowMarket error:', error);
      const msg = getErrorMessage(error);
      toast.error(msg ?? t('unfollow-failed'));
    }
    setIsLoading(false);
  };
  return (
    <AuthedButton
      {...props}
      aria-label={isFollow ? 'Unfollow' : 'Follow'}
      ref={ref}
      showAuth={false}
      showLoading={false}
      variant={props.variant ?? 'outline'}
      size={props.size ?? 'icon'}
      className={cn('rounded-full peer', props.className)}
      disabled={props.disabled || isLoading}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        if (isFollow) handleUnFollow();
        else handleFollow();
        props.onClick?.(e);
      }}
    >
      {isLoading ? (
        <SpanLoading />
      ) : isFollow ? (
        <>{isHover ? watchIcon : watchedIcon}</>
      ) : (
        <>{isHover ? watchingIcon : watchIcon}</>
      )}
    </AuthedButton>
  );
};
